import React, { useMemo, useState } from 'react';
import { Badge, Form } from 'react-bootstrap';
import {
  isBlocked,
  getBlockers,
  isTerminal,
  TERMINAL_STATUSES_BY_TYPE,
} from '../utils/taskTree';

// The Tasks panel is laid out as an organized to-do list grouped by the
// kind of work the homeowner is tracking. The order below reflects the
// natural sequence of a renovation: settle the open decisions, line up
// the people, line up the stuff, book the dates, do the work, then
// verify it.
const GROUP_ORDER = ['decisions', 'source_labor', 'purchases', 'scheduling', 'building', 'qa'];

const GROUP_META = {
  decisions:    { icon: 'bi-ui-checks', label: 'Decisions', color: '#C4612E' },
  source_labor: { icon: 'bi-people', label: 'Source labor', color: '#0EA5E9' },
  purchases:    { icon: 'bi-cart', label: 'Purchases', color: '#0284C7' },
  scheduling:   { icon: 'bi-calendar-event', label: 'Scheduling', color: '#A855F7' },
  building:     { icon: 'bi-wrench', label: 'Building', color: '#6B7280' },
  qa:           { icon: 'bi-clipboard-check', label: 'QA', color: '#059669' },
};

// Type → icon/color for the small leading icon on each task card.
const TYPE_META = {
  work: { icon: 'bi-wrench', label: 'Work', color: '#6B7280' },
  decision: { icon: 'bi-ui-checks', label: 'Decision', color: '#C4612E' },
  purchase: { icon: 'bi-cart', label: 'Purchase', color: '#0EA5E9' },
  scheduling: { icon: 'bi-calendar-event', label: 'Schedule', color: '#A855F7' },
  qa: { icon: 'bi-clipboard-check', label: 'QA', color: '#059669' },
  approval: { icon: 'bi-patch-check', label: 'Approval', color: '#DC2626' },
};

const STATUS_VARIANTS = {
  complete: 'success',
  passed: 'success',
  approved: 'success',
  delivered: 'success',
  in_progress: 'primary',
  scheduled: 'primary',
  ordered: 'primary',
  submitted: 'primary',
  needs_details: 'warning',
  failed: 'danger',
  rejected: 'danger',
  blocked: 'danger',
  na: 'secondary',
  pending: 'secondary',
};

// Fallback heuristic for older purchases that pre-date the agent's
// `purchase.item_kind` field. The agent now classifies every purchase as
// either "major_item" or "building_material" when generating the shopping
// list (and when adding ad-hoc purchases later); this keyword list only
// kicks in when that field is missing.
const BUILDING_MATERIAL_PATTERNS = [
  /caulk/i,
  /sand\s*paper/i,
  /\bsanding\b/i,
  /painters?\s*tape/i,
  /\btape\b/i,
  /screws?\b/i,
  /\bnails?\b/i,
  /\banchors?\b/i,
  /\bbolts?\b/i,
  /fasteners?/i,
  /\bprimer\b/i,
  /joint\s*compound/i,
  /spackle/i,
  /\bgrout\b/i,
  /thin[\s-]*set/i,
  /\bmortar\b/i,
  /sealant/i,
  /\bputty\b/i,
  /\bshims?\b/i,
  /drop\s*cloth/i,
  /\brags?\b/i,
  /weather[\s-]*strip/i,
  /\bglue\b/i,
  /adhesive/i,
  /plastic\s*sheet/i,
  /masking/i,
];

function isBuildingMaterial(task) {
  if (!task || task.type !== 'purchase') return false;
  const kind = task.purchase?.item_kind;
  if (kind === 'building_material') return true;
  if (kind === 'major_item') return false;
  // No agent-supplied classification — fall back to keyword detection so
  // legacy purchases still get bucketed sensibly.
  const haystack = `${task.title || ''} ${task.category || ''} ${task.description || ''}`;
  return BUILDING_MATERIAL_PATTERNS.some((re) => re.test(haystack));
}

function isSourceLabor(task) {
  if (!task || task.type !== 'work') return false;
  return /^source\s+labor\b/i.test(task.title || '');
}

function classifyGroup(task) {
  if (!task) return null;
  // Milestones are structural and deliveries are tracked elsewhere — both are
  // intentionally hidden from the to-do list view.
  if (task.type === 'milestone' || task.type === 'delivery') return null;
  if (task.type === 'decision') return 'decisions';
  if (task.type === 'purchase') return 'purchases';
  if (task.type === 'scheduling') return 'scheduling';
  if (task.type === 'qa' || task.type === 'approval') return 'qa';
  if (task.type === 'work') {
    return isSourceLabor(task) ? 'source_labor' : 'building';
  }
  return null;
}

function statusLabel(status) {
  return String(status || 'pending').replace(/_/g, ' ');
}

function statusVariant(status) {
  return STATUS_VARIANTS[status] || 'secondary';
}

function nextToggleStatus(task) {
  const terminals = TERMINAL_STATUSES_BY_TYPE[task.type] || ['complete'];
  return isTerminal(task) ? 'pending' : terminals[0];
}

function SpecLine({ spec }) {
  if (!spec) return null;
  return (
    <div className="task-spec-line small text-muted">
      <span className="fw-semibold">{spec.label || spec.key}:</span>{' '}
      <span>{spec.value || 'TBD'}</span>
    </div>
  );
}

function WorkBody({ task }) {
  const specs = task.work?.specifications || [];
  const filled = specs.filter((s) => s && s.value).length;
  return (
    <div className="task-body">
      {specs.length > 0 && (
        <div className="mb-1">
          <Badge bg="light" text="dark" className="me-2">
            {filled}/{specs.length} specs filled
          </Badge>
          {task.work?.priority && <Badge bg="light" text="dark">priority: {task.work.priority}</Badge>}
        </div>
      )}
      {specs.slice(0, 5).map((s, i) => <SpecLine key={i} spec={s} />)}
      {specs.length > 5 && <div className="small text-muted">+{specs.length - 5} more</div>}
    </div>
  );
}

function DecisionBody({ task }) {
  const d = task.decision || {};
  const options = d.options || [];
  return (
    <div className="task-body">
      {d.selected_option ? (
        <div className="small">
          <span className="text-muted">Chosen: </span>
          <span className="fw-semibold">{d.selected_option}</span>
        </div>
      ) : (
        <div className="small text-muted">
          {options.length} option{options.length === 1 ? '' : 's'} available
          {d.spec_label ? ` · ${d.spec_label}` : ''}
          {d.due_by ? ` · due ${d.due_by}` : ''}
        </div>
      )}
    </div>
  );
}

function PurchaseBody({ task }) {
  const p = task.purchase || {};
  return (
    <div className="task-body small text-muted">
      {p.quantity != null && <span className="me-2">Qty: {p.quantity}</span>}
      {p.estimated_cost != null && <span className="me-2">Cost: {p.estimated_cost}</span>}
      {p.vendor && <span className="me-2">Vendor: {p.vendor}</span>}
      {p.on_shopping_list && <Badge bg="light" text="dark" className="ms-1">shopping list</Badge>}
    </div>
  );
}

function SchedulingBody({ task }) {
  const s = task.scheduling || {};
  return (
    <div className="task-body small text-muted">
      {s.vendor_type && <span className="me-2">Vendor: {s.vendor_type}</span>}
      {s.scheduled_for && <span className="me-2">When: {s.scheduled_for}</span>}
      {s.contact && <span className="me-2">Contact: {s.contact}</span>}
    </div>
  );
}

function QABody({ task }) {
  const q = task.qa || {};
  return (
    <div className="task-body small">
      {q.acceptance_criteria && (
        <div className="text-muted"><span className="fw-semibold">Criteria:</span> {q.acceptance_criteria}</div>
      )}
      {q.inspector_notes && (
        <div className="text-muted"><span className="fw-semibold">Notes:</span> {q.inspector_notes}</div>
      )}
    </div>
  );
}

function ApprovalBody({ task }) {
  const a = task.approval || {};
  return (
    <div className="task-body small text-muted">
      {a.approver && <span className="me-2">Approver: {a.approver}</span>}
      {a.document_url && (
        <a href={a.document_url} target="_blank" rel="noopener noreferrer">Document</a>
      )}
    </div>
  );
}

function BodyForType({ task }) {
  switch (task.type) {
    case 'work': return <WorkBody task={task} />;
    case 'decision': return <DecisionBody task={task} />;
    case 'purchase': return <PurchaseBody task={task} />;
    case 'scheduling': return <SchedulingBody task={task} />;
    case 'qa': return <QABody task={task} />;
    case 'approval': return <ApprovalBody task={task} />;
    default: return null;
  }
}

function BlockedChip({ task, allTasks }) {
  const blockers = getBlockers(task, allTasks);
  if (blockers.length === 0) return null;
  const names = blockers.map((b) => b.title).join(', ');
  return (
    <Badge bg="warning" text="dark" className="task-blocked-chip" title={names}>
      <i className="bi bi-lock me-1" />
      blocked by {blockers.length === 1 ? blockers[0].title : `${blockers.length} tasks`}
    </Badge>
  );
}

function TaskCard({ task, allTasks, onToggle, disabled }) {
  const meta = TYPE_META[task.type] || { icon: 'bi-circle', label: task.type, color: '#9CA3AF' };
  const checked = isTerminal(task);
  return (
    <div
      className={`task-card status-${task.status} type-${task.type}${checked ? ' is-checked' : ''}`}
      style={{ borderLeftColor: meta.color }}
      data-testid={`task-${task.id}`}
    >
      <Form.Check
        type="checkbox"
        id={`task-check-${task.id}`}
        className="task-check"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle && onToggle(task)}
        aria-label={checked ? `Mark ${task.title} incomplete` : `Mark ${task.title} complete`}
        data-testid={`task-check-${task.id}`}
      />
      <div className="task-card-content">
        <div className="task-header">
          <i className={`bi ${meta.icon} task-type-icon`} style={{ color: meta.color }} />
          <span className="task-title">{task.title}</span>
          {task.category && <span className="task-category small text-muted">· {task.category}</span>}
          <div className="task-header-right">
            <BlockedChip task={task} allTasks={allTasks} />
            <Badge bg={statusVariant(task.status)} className="task-status-chip">
              {statusLabel(task.status)}
            </Badge>
          </div>
        </div>
        {task.description && (
          <div className="task-description small text-muted">{task.description}</div>
        )}
        <BodyForType task={task} />
      </div>
    </div>
  );
}

function GroupHeader({ groupKey, doneCount, totalCount, isOpen, onToggle, count, label, icon, color }) {
  return (
    <button
      type="button"
      className="task-group-header"
      onClick={onToggle}
      aria-expanded={isOpen}
      data-testid={`group-toggle-${groupKey}`}
    >
      <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-right'} task-group-chevron`} />
      <i className={`bi ${icon} me-2`} style={{ color }} />
      <span className="task-group-title">{label}</span>
      <div className="task-group-header-right">
        <Badge bg="light" text="dark" className="task-group-progress">
          {doneCount}/{totalCount}
        </Badge>
        {count != null && count !== totalCount && (
          <span className="small text-muted">{count} shown</span>
        )}
      </div>
    </button>
  );
}

function PurchasesGroupBody({ tasks, allTasks, onToggle, materialsOpen, onToggleMaterials, readOnly }) {
  const materials = tasks.filter(isBuildingMaterial);
  const majors = tasks.filter((t) => !isBuildingMaterial(t));
  const materialsDone = materials.filter(isTerminal).length;

  return (
    <div className="task-group-body">
      {majors.length > 0 ? (
        majors.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            allTasks={allTasks}
            onToggle={onToggle}
            disabled={readOnly}
          />
        ))
      ) : (
        <div className="small text-muted px-1 py-2">No major items yet.</div>
      )}

      {materials.length > 0 && (
        <div className="task-subgroup" data-testid="materials-subgroup">
          <button
            type="button"
            className="task-subgroup-header"
            onClick={onToggleMaterials}
            aria-expanded={materialsOpen}
            data-testid="materials-toggle"
          >
            <i className={`bi ${materialsOpen ? 'bi-chevron-down' : 'bi-chevron-right'} task-group-chevron`} />
            <i className="bi bi-tools me-2" style={{ opacity: 0.75 }} />
            <span className="task-subgroup-title">Building materials</span>
            <span className="text-muted small ms-2">contractor-supplied</span>
            <div className="task-group-header-right">
              <Badge bg="light" text="dark" className="task-group-progress">
                {materialsDone}/{materials.length}
              </Badge>
            </div>
          </button>
          {materialsOpen && (
            <div className="task-subgroup-body">
              {materials.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  allTasks={allTasks}
                  onToggle={onToggle}
                  disabled={readOnly}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TaskChainPanel({ tasks = [], onTaskToggle, readOnly = false }) {
  const [search, setSearch] = useState('');
  // User overrides for group open/closed state. Anything not in this map
  // falls back to the per-group default below — that lets the Decisions
  // group auto-collapse once every decision is settled, while still
  // respecting an explicit toggle from the user.
  const [groupOverrides, setGroupOverrides] = useState(() => new Map());
  // Building materials default collapsed — they're noise compared to the
  // big-ticket items the homeowner is actually shopping for.
  const [materialsOpen, setMaterialsOpen] = useState(false);

  // Filter delivery + milestone out entirely; deliveries no longer appear in
  // the UI, and milestones never had cards in this view.
  const visibleTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (tasks || []).filter((t) => {
      if (!t) return false;
      if (t.type === 'delivery' || t.type === 'milestone') return false;
      if (!classifyGroup(t)) return false;
      if (!q) return true;
      return `${t.title} ${t.category || ''} ${t.description || ''}`.toLowerCase().includes(q);
    });
  }, [tasks, search]);

  const grouped = useMemo(() => {
    const map = new Map(GROUP_ORDER.map((g) => [g, []]));
    for (const task of visibleTasks) {
      const g = classifyGroup(task);
      if (!g) continue;
      map.get(g).push(task);
    }
    function cmp(a, b) {
      const aDone = isTerminal(a) ? 1 : 0;
      const bDone = isTerminal(b) ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      const aBlocked = isBlocked(a, tasks) ? 1 : 0;
      const bBlocked = isBlocked(b, tasks) ? 1 : 0;
      if (aBlocked !== bBlocked) return aBlocked - bBlocked;
      const oa = typeof a.order === 'number' ? a.order : 0;
      const ob = typeof b.order === 'number' ? b.order : 0;
      if (oa !== ob) return oa - ob;
      return (a.createdAt || 0) - (b.createdAt || 0);
    }
    for (const arr of map.values()) arr.sort(cmp);
    return map;
  }, [visibleTasks, tasks]);

  // Per-group totals computed from the unfiltered task list so the header
  // counters remain stable while the user types into the search box.
  const allByGroup = useMemo(() => {
    const map = new Map(GROUP_ORDER.map((g) => [g, []]));
    for (const t of tasks || []) {
      const g = classifyGroup(t);
      if (!g) continue;
      map.get(g).push(t);
    }
    return map;
  }, [tasks]);

  function defaultGroupOpen(groupKey) {
    if (groupKey === 'decisions') {
      const decisions = allByGroup.get('decisions') || [];
      if (decisions.length === 0) return true;
      // Auto-collapse once every decision is settled; otherwise stay open
      // so the homeowner sees what still needs choosing.
      return !decisions.every(isTerminal);
    }
    return true;
  }

  function isGroupOpen(groupKey) {
    return groupOverrides.has(groupKey)
      ? groupOverrides.get(groupKey)
      : defaultGroupOpen(groupKey);
  }

  function toggleGroup(groupKey) {
    const currentlyOpen = isGroupOpen(groupKey);
    setGroupOverrides((prev) => {
      const next = new Map(prev);
      next.set(groupKey, !currentlyOpen);
      return next;
    });
  }

  function handleToggle(task) {
    if (!onTaskToggle) return;
    const nextStatus = nextToggleStatus(task);
    onTaskToggle(task.id, nextStatus);
  }

  const hasAnyTasks = (tasks || []).some((t) => t && classifyGroup(t));

  return (
    <div className="task-chain-panel h-100 d-flex flex-column">
      <div className="task-chain-toolbar p-3 border-bottom">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Form.Control
            size="sm"
            type="search"
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 260 }}
          />
        </div>
      </div>

      <div className="task-chain-list flex-grow-1 overflow-auto p-3">
        {!hasAnyTasks ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-check2-square display-4 d-block mb-2" />
            <p>No tasks yet. Start a chat and Plum will build your task list.</p>
          </div>
        ) : (
          GROUP_ORDER.map((groupKey) => {
            const groupTotal = allByGroup.get(groupKey) || [];
            if (groupTotal.length === 0) return null;
            const visibleInGroup = grouped.get(groupKey) || [];
            const meta = GROUP_META[groupKey];
            const isOpen = isGroupOpen(groupKey);
            const doneCount = groupTotal.filter(isTerminal).length;
            return (
              <div key={groupKey} className="task-group" data-testid={`group-${groupKey}`}>
                <GroupHeader
                  groupKey={groupKey}
                  doneCount={doneCount}
                  totalCount={groupTotal.length}
                  count={visibleInGroup.length}
                  isOpen={isOpen}
                  onToggle={() => toggleGroup(groupKey)}
                  label={meta.label}
                  icon={meta.icon}
                  color={meta.color}
                />
                {isOpen && (
                  groupKey === 'purchases' ? (
                    <PurchasesGroupBody
                      tasks={visibleInGroup}
                      allTasks={tasks}
                      onToggle={handleToggle}
                      materialsOpen={materialsOpen}
                      onToggleMaterials={() => setMaterialsOpen((v) => !v)}
                      readOnly={readOnly || !onTaskToggle}
                    />
                  ) : (
                    <div className="task-group-body">
                      {visibleInGroup.length === 0 ? (
                        <div className="small text-muted px-1 py-2">
                          {search ? 'No tasks match the search.' : 'Nothing here yet.'}
                        </div>
                      ) : (
                        visibleInGroup.map((task) => (
                          <TaskCard
                            key={task.id}
                            task={task}
                            allTasks={tasks}
                            onToggle={handleToggle}
                            disabled={readOnly || !onTaskToggle}
                          />
                        ))
                      )}
                    </div>
                  )
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
